import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-amber.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/sprinter-limo-4.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/car-service-atlanta-to-lake-oconee";
const PAGE_TITLE = "Atlanta to Lake Oconee Car Service | Private Sprinter Transfer";
const PAGE_DESC =
  "Chauffeured Mercedes-Benz Sprinter transfers from Atlanta to Reynolds Lake Oconee, the Ritz-Carlton, Greensboro and Madison. Fixed rate. Call (678) 431-7222.";

const faqs = [
  {
    q: "How far is Lake Oconee from Atlanta?",
    a: "About 80 miles east on I-20, or roughly 90 minutes door to door from in-town Atlanta on a normal departure. Friday afternoons leaving the city are the exception, and we usually recommend departing before 3 p.m. or after 7 p.m. when the schedule allows.",
  },
  {
    q: "Do you have access to Reynolds Lake Oconee and the Ritz-Carlton?",
    a: "Yes. Reynolds is gated, so we pass the guest name, cottage or villa number, and arrival window to the gatehouse in advance. Ritz-Carlton Reynolds arrivals are handled at the main porte-cochère with bell staff.",
  },
  {
    q: "Is the chauffeur available while we're at the lake?",
    a: "On request. Golf groups and wedding parties often keep the vehicle on an as-directed basis for course transfers between the Reynolds courses, dinners at the National, and runs into Greensboro or Madison. We arrange the chauffeur's lodging and quote it into the daily rate.",
  },
  {
    q: "Can you carry golf bags for a foursome or a full group?",
    a: "Yes. The Executive Sprinter takes eight passengers with clubs and weekend luggage without stacking bags across the cabin. For twelve or more golfers we recommend a second vehicle rather than compressing the load.",
  },
  {
    q: "Do you handle wedding guest transfers at lake venues?",
    a: "Regularly. The usual pattern is a Sprinter running loops between the hotel block in Greensboro or Madison and the venue, plus a dedicated vehicle for the couple and immediate family.",
  },
  {
    q: "Can you continue on to Augusta or Athens?",
    a: "Yes. Lake Oconee sits between both. We run Atlanta to Augusta transfers during Masters week and Athens transfers on football weekends, often with a Lake Oconee stop on the way.",
  },
];

export const Route = createFileRoute("/car-service-atlanta-to-lake-oconee")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "atlanta to lake oconee car service, reynolds lake oconee transportation, ritz-carlton reynolds car service, greensboro ga chauffeur, atlanta to madison ga private transfer",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Atlanta to Lake Oconee Car Service",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Long Distance Private Car Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Greensboro" },
              { type: "City", name: "Madison" },
              { type: "City", name: "Eatonton" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Atlanta • I-20 East • Lake Oconee"
      h1="Private Car Service from Atlanta to Lake Oconee"
      intro="Your lake weekend should start together. I'll arrange your Atlanta pickup for Reynolds Lake Oconee, the Ritz-Carlton, Greensboro or Madison, with room for your group, clubs and luggage."
      heroImage={heroImage}
      heroAlt="Executive Mercedes-Benz Sprinter cabin prepared for a Lake Oconee weekend transfer from Atlanta"
      midImage={midImage}
      midAlt="Lounge seating inside a Mercedes-Benz Sprinter used for Lake Oconee transfers"
      sectionOneTitle="Start Your Lake Weekend Together"
      sectionOneBody={[
        "Different departure times and golf bags can turn a simple weekend into a logistics project. We collect your group from multiple Atlanta addresses and bring everyone to the same door.",
        "My chauffeur handles the clubs, luggage and Reynolds gatehouse arrangements. I'll confirm your door-to-door rate in writing, including the chauffeur's return.",
        "Want the vehicle to stay? I'll quote an as-directed daily rate before you book.",
      ]}
      bulletsTitle="What the Lake Oconee Transfer Covers"
      bulletsIntro="Gate access, golf bags, and a chauffeur who knows the property layout."
      bullets={[
        "Fixed door-to-door rate confirmed in writing, return leg included",
        "Multiple pickup addresses across Buckhead, Midtown, Sandy Springs, and Alpharetta",
        "Reynolds Lake Oconee gatehouse clearance arranged in advance",
        "Ritz-Carlton Reynolds porte-cochère arrivals with bell staff",
        "Full capacity for golf bags plus weekend luggage",
        "As-directed daily rate if the vehicle stays with you at the lake",
        "Onward continuation to Augusta, Athens, or Milledgeville",
      ]}
      coverageTitle="Lake Oconee Area Destinations"
      coverageIntro="Properties, venues, and towns we serve on the I-20 East corridor."
      coverage={[
        "Reynolds Lake Oconee",
        "The Ritz-Carlton Reynolds",
        "The Oconee & Great Waters courses",
        "Cuscowilla",
        "Harbor Club",
        "Greensboro, GA",
        "Madison, GA",
        "Eatonton",
        "Lake Sinclair & Milledgeville",
        "Athens",
        "Augusta",
        "Conyers & Covington stopovers",
      ]}
      routeTable={{
        title: "I-20 East Distances and Drive Times",
        intro:
          "Timings our chauffeurs plan against from in-town Atlanta, with the Friday exodus accounted for separately.",
        rows: [
          {
            route: "Atlanta → Reynolds Lake Oconee",
            distance: "~80 mi",
            time: "1h 20m – 2h 00m",
            notes: "I-20 E to Exit 130. Gate list submitted before departure.",
          },
          {
            route: "Atlanta → Ritz-Carlton Reynolds",
            distance: "~83 mi",
            time: "1h 25m – 2h 05m",
            notes: "Main porte-cochère arrival; luggage handed to bell staff.",
          },
          {
            route: "Atlanta → Greensboro, GA",
            distance: "~76 mi",
            time: "1h 15m – 1h 55m",
            notes: "Common hotel-block town for lake weddings and guest loops.",
          },
          {
            route: "Atlanta → Madison, GA",
            distance: "~62 mi",
            time: "1h 05m – 1h 40m",
            notes: "Historic district venues; downtown streets are tight for larger vehicles.",
          },
          {
            route: "Atlanta → Cuscowilla / Eatonton",
            distance: "~90 mi",
            time: "1h 35m – 2h 10m",
            notes: "US-441 S after I-20. Rural two-lane sections at the end.",
          },
          {
            route: "Lake Oconee → Augusta",
            distance: "~70 mi",
            time: "1h 10m – 1h 45m",
            notes: "Masters week continuation; timings shift heavily that week.",
          },
          {
            route: "Lake Oconee as-directed day",
            distance: "Varies",
            time: "6–10 hr block",
            notes: "Course transfers, dinners, and town runs with the vehicle staying on property.",
          },
        ],
        footnote:
          "Friday departures from Atlanta between 3 and 7 p.m. add 30–40 minutes; we advise a departure window when you book rather than after.",
      }}
      detailSections={[
        {
          title: "Golf Weekends at Reynolds",
          body: [
            "Reynolds spreads across several courses and clubhouses, and the distances between them are longer than first-time guests expect. Groups that arrive in their own cars end up shuttling themselves between tee times, dinner at the National, and their villas.",
            "Keeping one Sprinter and chauffeur on an as-directed basis removes that entirely, clubs stay loaded, nobody drives after dinner, and the group moves as one.",
          ],
        },
        {
          title: "Lake and Historic-District Weddings",
          list: [
            "Guest loops between the Greensboro or Madison hotel block and the venue.",
            "A dedicated vehicle for the couple and immediate family, held all evening.",
            "Rehearsal dinner and welcome-party transfers the night before.",
            "Sunday morning departures back to Atlanta or to ATL for outbound flights.",
          ],
        },
        {
          title: "Choosing the Vehicle",
          list: [
            "2–4 passengers with clubs, Suburban SUV.",
            "Up to 13 passengers, Executive Sprinter with facing captain's chairs and full luggage capacity.",
            "9–14 passengers, Mercedes-Benz Sprinter Limousine with lounge seating and wet bar.",
            "15+ passengers, two Sprinters travelling together under one lead chauffeur.",
          ],
        },
      ]}
      gallery={{
        title: "Vehicles on the Lake Oconee Route",
        intro: "Room for the group, the clubs, and the luggage in one vehicle.",
        images: [
          { src: galleryA, alt: "Executive Mercedes-Benz Sprinter interior for a Lake Oconee golf weekend", caption: "Executive Sprinter, up to 13" },
          { src: galleryB, alt: "Mercedes-Benz Sprinter Limousine cabin for Lake Oconee wedding groups", caption: "Sprinter Limousine, up to 14" },
          { src: galleryC, alt: "Suburban SUV interior for small-group Lake Oconee transfers", caption: "Suburban SUV, up to 6 (captain seats) or 7 (bench seats)" },
        ],
      }}
      faqs={faqs}
      faqTitle="Atlanta to Lake Oconee Car Service FAQs"
      relatedTitle="Related Destination Services"
      related={[
        { to: "/car-service-atlanta-to-savannah", title: "Atlanta to Savannah", desc: "Coastal Georgia and Lowcountry transfers." },
        { to: "/car-service-atlanta-to-chattanooga", title: "Atlanta to Chattanooga", desc: "I-75 North corridor and Lookout Mountain." },
        { to: "/wedding-transportation-atlanta", title: "Wedding Transportation", desc: "Bridal party transfers and guest loops." },
      ]}
      ctaTitle="Text Me Your Lake Weekend Plans"
      ctaBody="Text me your date, pickup addresses, group size and whether you'd like the vehicle to stay. I'll confirm your rate in writing and arrange the gate list."
    />
  );
}
